import { useState } from 'react';
import { DocumentScannerOutlined } from '@mui/icons-material';
import { Autocomplete, Box, Chip, FormControlLabel, Link, Switch, TextField, Typography } from '@mui/material';
import { useCreate, useGo } from '@refinedev/core';
import { Create } from '@refinedev/mui';
import { useForm } from '@refinedev/react-hook-form';
import { format } from 'date-fns';
import {
  Controller,
  FieldValues,
  FormProvider,
  SubmitHandler,
  useController,
  useFormContext,
  useWatch
} from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';

import { Dropzone, RadioGroupInput, TextFieldInput } from '@attackiq/components';
import { ATTACKIQ_ID } from '@attackiq/constants';
import { fileToBase64 } from '@attackiq/utils';

import { ShowInAppFormGroup } from '../../components';
import { MarkdownEditor } from '../../components/inputs';
import { New } from '../../types';

export const FeaturedInput = () => {
  const { control } = useFormContext();
  const { field } = useController({
    name: 'featured',
    control
  });

  const options = [
    { model: false, displayName: 'No' },
    { model: true, displayName: 'Yes' }
  ];

  return (
    <Autocomplete
      renderInput={params => <TextField {...params} label="Featured" />}
      options={options}
      onChange={(_e, value) => {
        field.onChange(value?.model);
      }}
      value={options.find(option => option.model === field.value)}
      getOptionLabel={option => option.displayName}
    />
  );
};

export const ActiveInput = () => {
  const { control } = useFormContext();
  const { field } = useController({
    name: 'active',
    control
  });

  const options = [
    { model: false, displayName: 'No' },
    { model: true, displayName: 'Yes' }
  ];

  return (
    <Autocomplete
      renderInput={params => <TextField {...params} label="Active" />}
      options={options}
      onChange={(_e, value) => {
        field.onChange(value?.model);
      }}
      value={options.find(option => option.model === field.value)}
      getOptionLabel={option => option.displayName}
    />
  );
};

export const TypeInput = () => {
  const options = [
    { value: 'whats_new', label: "What's New" },
    { value: 'release_note', label: 'Release Note' }
  ];

  return <RadioGroupInput name="type" options={options} />;
};

const NewCreate = () => {
  const { mutateAsync: createMutateAsync } = useCreate();

  const methods = useForm<New>({
    refineCoreProps: { action: 'create' },
    defaultValues: {
      content_id: '',
      content_type_name: '',
      description: '',
      url: '',
      company: ATTACKIQ_ID,
      featured: false,
      cover_image: '',
      date: '',
      id: '',
      modules: [],
      scenario_tag_ids: [],
      title: '',
      active: false,
      type: 'whats_new',
      order: 0
    }
  });

  const { handleSubmit, setValue, control } = methods;
  const selectedType = useWatch({ name: 'type', control });

  const go = useGo();

  const [selectedFile, setSelectedFile] = useState<File | undefined | null>(null);
  const [urlEnabled, setUrlEnabled] = useState(false);

  const onSubmit: SubmitHandler<New> = async newContent => {
    const payload = {
      ...newContent,
      id: uuidv4(),
      date: format(new Date(), 'yyyy-MM-dd')
    };

    if (!urlEnabled) {
      payload.cover_image = selectedFile ? await fileToBase64(selectedFile) : '';
    }

    const { data: newlyCreatedNew } = await createMutateAsync({
      resource: 'whats_new',
      values: payload
    });

    if (!newlyCreatedNew.id) return;

    go({
      to: {
        resource: 'whats_new',
        action: 'edit',
        id: newlyCreatedNew.id
      }
    });
  };

  return (
    <FormProvider {...methods}>
      <form
        name="create-module-form"
        onSubmit={handleSubmit(onSubmit as SubmitHandler<FieldValues>)}
        autoComplete="off"
      >
        <Create saveButtonProps={{ type: 'submit' }}>
          <Box display="flex" flexDirection="column" gap={2}>
            <ActiveInput />
            <TypeInput />
            <TextFieldInput color="primary" name="title" label="Title" rules={{ required: 'Title is required' }} />
            {selectedType === 'whats_new' && (
              <>
                <TextFieldInput
                  color="primary"
                  name="content_id"
                  label="Content ID"
                  rules={{ required: 'Content ID is required' }}
                />
                <TextFieldInput
                  color="primary"
                  name="content_type_name"
                  label="Content Type Name"
                  rules={{ required: 'Content Type Name is required' }}
                />
                <TextFieldInput color="primary" name="url" label="URL" rules={{ required: 'URL is required' }} />
              </>
            )}
            <TextFieldInput
              color="primary"
              name="order"
              label="Order"
              type="number"
              rules={{ required: 'Order is required' }}
            />
            <Controller
              name="description"
              control={methods.control}
              rules={{ required: 'Description is required' }}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <Box>
                  <Typography variant="body2" sx={{ mb: 1, color: error ? 'error.main' : 'text.primary' }}>
                    Description {error && <span style={{ color: 'red' }}>*</span>}
                  </Typography>
                  <MarkdownEditor value={value || ''} onChange={onChange} readonly={false} />
                  {error && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                      {error.message}
                    </Typography>
                  )}
                </Box>
              )}
            />
            <ModulesInput />
            {selectedType === 'whats_new' && <ScenarioTagIDSInput />}
            <FeaturedInput />
            {selectedType === 'whats_new' && (
              <>
                <FormControlLabel
                  label="Use URL to upload cover image"
                  control={
                    <Switch
                      value={urlEnabled}
                      onChange={e => {
                        setValue('cover_image', '');
                        setUrlEnabled(e.target.checked);
                      }}
                    />
                  }
                />
                {urlEnabled ? (
                  <TextFieldInput
                    color="primary"
                    name="cover_image"
                    label="Cover Image URL"
                    rules={{
                      required: 'Cover Image is required',
                      pattern: { value: /https?:\/\/.*/, message: 'Invalid URL' }
                    }}
                  />
                ) : (
                  <Dropzone
                    rules={{
                      accept: '.png, .jpg, .jpeg, .svg, .webp'
                    }}
                    onDropAccepted={files => {
                      setSelectedFile(files[0]);
                    }}
                    width="100%"
                    height={152}
                  >
                    <Dropzone.Body>
                      <>
                        <Typography>
                          <Link component="span">Click to upload</Link> or drag and drop
                        </Typography>
                        <Typography color="text.secondary">
                          .png, .jpg, .jpeg, .svg, .webp files supported only (max. 3MB)
                        </Typography>
                      </>
                    </Dropzone.Body>

                    <Dropzone.Cover
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      flexDirection="column"
                      bgcolor="background.paper"
                      in={!!selectedFile}
                    >
                      <DocumentScannerOutlined color="primary" />
                      <Typography>{selectedFile?.name}</Typography>
                    </Dropzone.Cover>
                  </Dropzone>
                )}
              </>
            )}
            <ShowInAppFormGroup />
          </Box>
        </Create>
      </form>
    </FormProvider>
  );
};

export const ScenarioTagIDSInput = () => {
  const { field } = useController({ name: 'scenario_tag_ids' });

  return (
    <Autocomplete
      options={[]}
      freeSolo
      sx={{ flex: 1 }}
      disableCloseOnSelect
      renderTags={(tagValue, getTagProps) => {
        return tagValue.map((option, index) => <Chip {...getTagProps({ index })} key={option} label={option} />);
      }}
      renderInput={props => (
        <TextField
          {...props}
          variant="outlined"
          color="primary"
          label={'Scenario Tag IDS'}
          helperText="Press enter after each ID"
        />
      )}
      value={field.value ?? []}
      onChange={(_e, v) => {
        field.onChange(v);
      }}
      multiple
    />
  );
};

export const ModulesInput = () => {
  const { field } = useController({ name: 'modules' });

  return (
    <Autocomplete
      options={[]}
      freeSolo
      sx={{ flex: 1 }}
      disableCloseOnSelect
      renderTags={(tagValue, getTagProps) => {
        return tagValue.map((option, index) => <Chip {...getTagProps({ index })} key={option} label={option} />);
      }}
      renderInput={props => (
        <TextField
          {...props}
          variant="outlined"
          color="primary"
          label={'Modules'}
          helperText="Press enter after each Module"
        />
      )}
      value={field.value ?? []}
      onChange={(_e, v) => {
        field.onChange(v);
      }}
      multiple
    />
  );
};

export default NewCreate;
